import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';

// --- CONFIGURACIÓN DE FIREBASE (Usa variables inyectadas del entorno) ---
// Las variables __app_id, __firebase_config, y __initial_auth_token son proporcionadas por el entorno de Canvas.
const appId = typeof __app_id !== 'undefined' ? __app_id : 'memory-game-default-app';
const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;
// Intenta parsear la configuración. Usa un objeto vacío si no está disponible (aunque debería estarlo en runtime).
const firebaseConfig = typeof __firebase_config !== 'undefined' 
    ? JSON.parse(__firebase_config) 
    : {};

// Estructura de iconos para los pares de memoria (usando SVG en línea)
const icons = [
  'M19 4L15 4L15 20L19 20L19 4ZM13 4L9 4L9 20L13 20L13 4ZM7 4L3 4L3 20L7 20L7 4Z', // Bar Chart
  'M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z', // Alert Circle
  'M16 4h-2a2 2 0 0 0-2 2v14h4a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2ZM8 14H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4', // Anchor
  'M10 20C10 20.55 10.45 21 11 21H13C13.55 21 14 20.55 14 20H10ZM12 4C8.68 4 6 6.68 6 10V17H18V10C18 6.68 15.32 4 12 4Z', // Bell
  'M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.59-5.58L4 12l8 8 8-8Z', // Arrow Down Circle
  'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z', // Info Circle
  'M22 12A10 10 0 0 1 12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12ZM17 12a5 5 0 1 0-10 0 5 5 0 0 0 10 0Z', // Target
  'M17 19l-5-5-5 5-2-2 7-7 7 7-2 2Z', // Triangle
];

const initialCards = icons.flatMap((icon, index) => {
  const id = crypto.randomUUID();
  return [
    { id: id + '-a', icon, isFlipped: false, isMatched: false, pairId: index },
    { id: id + '-b', icon, isFlipped: false, isMatched: false, pairId: index },
  ];
}).sort(() => Math.random() - 0.5); // Barajar inmediatamente

// Componente para un ícono SVG genérico
const IconSVG = ({ path, className = "h-6 w-6" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d={path} />
  </svg>
);

// Componente principal de la aplicación
const App = () => {
  const [cards, setCards] = useState(initialCards);
  // Almacena solo los IDs de las cartas volteadas
  const [flippedCards, setFlippedCards] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [pairsFound, setPairsFound] = useState(0);
  const [isBusy, setIsBusy] = useState(false);
  const [bestScore, setBestScore] = useState('Cargando...');
  const [firestoreStatus, setFirestoreStatus] = useState('Autenticando...');

  // Estados de Firebase/Auth/User
  const [db, setDb] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  // Referencia a Firestore para el puntaje.
  const bestScoreRef = useMemo(() => {
    if (db && userId) {
      // Ruta: /artifacts/{appId}/users/{userId}/gameData/memoryGame
      return doc(db, 'artifacts', appId, 'users', userId, 'gameData', 'memoryGame');
    }
    return null;
  }, [db, userId]);

  // --- LÓGICA DE FIREBASE Y AUTENTICACIÓN ---

  useEffect(() => {
    // 1. Inicializar Firebase
    if (Object.keys(firebaseConfig).length === 0 || !firebaseConfig.projectId) {
        setFirestoreStatus('Error: Configuración de Firebase no disponible.');
        setIsAuthReady(true);
        return;
    }
    
    try {
      const app = initializeApp(firebaseConfig);
      const firestore = getFirestore(app);
      const userAuth = getAuth(app);
      
      setDb(firestore);
      
      // 2. Autenticación y listener de estado
      const authenticate = async () => {
        try {
          // Usar el token proporcionado o iniciar sesión anónimamente
          if (initialAuthToken) {
            await signInWithCustomToken(userAuth, initialAuthToken);
          } else {
            await signInAnonymously(userAuth);
          }
        } catch (error) {
          console.error("Error en la autenticación de Firebase:", error);
          setFirestoreStatus('Error de autenticación. Intentos no se guardarán.');
        }
      };
      
      authenticate();

      const unsubscribe = onAuthStateChanged(userAuth, (user) => {
        if (user) {
          setUserId(user.uid);
          setFirestoreStatus(`Autenticación exitosa. User ID: ${user.uid.substring(0, 8)}...`);
        } else {
          setUserId(crypto.randomUUID()); // ID temporal
          setFirestoreStatus('Autenticación anónima activa.');
        }
        setIsAuthReady(true); // La autenticación ha terminado su chequeo inicial
      });

      return () => unsubscribe();

    } catch (error) {
      console.error("Error al inicializar Firebase:", error);
      setFirestoreStatus(`Error al inicializar Firebase: ${error.message}`);
      setIsAuthReady(true);
    }
  }, []); // Solo se ejecuta una vez al montar el componente

  // --- LÓGICA DE FIREBASE: LECTURA Y ESCRITURA ---

  // Efecto para escuchar el Mejor Puntaje en tiempo real
  useEffect(() => {
    if (!isAuthReady || !bestScoreRef) {
      setBestScore('Cargando...');
      return;
    }

    // onSnapshot escucha los cambios en el documento en tiempo real
    const unsubscribe = onSnapshot(bestScoreRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data && typeof data.bestScore === 'number') {
          setBestScore(data.bestScore);
        } else {
          setBestScore('N/A');
        }
      } else {
        setBestScore('N/A');
      }
    }, (error) => {
      console.error("Error al escuchar Firestore:", error);
      setFirestoreStatus('Error al leer puntaje. Revisar consola.');
      setBestScore('Error');
    });

    return () => unsubscribe();

  }, [isAuthReady, bestScoreRef]);

  // Función para guardar el puntaje
  const saveBestScore = useCallback(async (newScore) => {
    if (!bestScoreRef || typeof newScore !== 'number') return;

    try {
      const docSnap = await getDoc(bestScoreRef);
      let currentBestScore = Infinity;

      if (docSnap.exists() && typeof docSnap.data().bestScore === 'number') {
        currentBestScore = docSnap.data().bestScore;
      }
      
      if (newScore < currentBestScore) {
        await setDoc(bestScoreRef, { bestScore: newScore, date: new Date().toISOString() }, { merge: true });
        setFirestoreStatus(`🏆 Nuevo mejor puntaje (${newScore}) guardado.`);
      } else {
        setFirestoreStatus(`Puntaje (${newScore}) no supera al mejor (${currentBestScore}).`);
      }
    } catch (error) {
      console.error("Error al guardar el puntaje en Firestore:", error);
      setFirestoreStatus(`Error al guardar: ${error.message || 'Desconocido'}`);
    }
  }, [bestScoreRef]);

  // --- LÓGICA DEL JUEGO ---

  const handleCardClick = (clickedCard) => {
    // Si está ocupado, o la carta ya está volteada/emparejada, salir
    if (isBusy || clickedCard.isFlipped || clickedCard.isMatched) return;
    
    // Usar functional update para asegurar que la lógica de conteo sea precisa
    setFlippedCards(prevFlipped => {
        if (prevFlipped.length < 2) {
            // Voltear la carta en el estado principal
            setCards(prevCards =>
                prevCards.map(card =>
                    card.id === clickedCard.id ? { ...card, isFlipped: true } : card
                )
            );
            return [...prevFlipped, clickedCard.id];
        }
        return prevFlipped; // Ya hay dos cartas volteadas
    });
  };

  // Efecto para verificar el par de cartas
  useEffect(() => {
    if (flippedCards.length === 2) {
      setIsBusy(true);
      setAttempts(prev => prev + 1);

      // Buscar las cartas REALES y MÁS RECIENTES usando los IDs
      const card1 = cards.find(c => c.id === flippedCards[0]);
      const card2 = cards.find(c => c.id === flippedCards[1]);

      if (!card1 || !card2) {
          console.error("Error: No se pudieron encontrar ambas cartas para la verificación.");
          setIsBusy(false);
          setFlippedCards([]);
          return;
      }

      if (card1.pairId === card2.pairId) {
        // Coincidencia encontrada
        setCards(prevCards =>
          prevCards.map(card =>
            // Marcar como emparejado (y mantener volteado)
            card.pairId === card1.pairId ? { ...card, isMatched: true } : card
          )
        );
        setPairsFound(prev => prev + 1);
        setIsBusy(false);
        setFlippedCards([]);
      } else {
        // No coincidencia, voltear de nuevo después de 1 segundo
        setTimeout(() => {
          // Capturar los IDs aquí para que el timeout use la referencia correcta
          const [id1, id2] = flippedCards; 

          setCards(prevCards =>
            prevCards.map(card =>
              // Voltear ambas cartas usando sus IDs almacenados
              (card.id === id1 || card.id === id2) ? { ...card, isFlipped: false } : card
            )
          );
          setIsBusy(false);
          setFlippedCards([]);
        }, 1000);
      }
    }
  }, [flippedCards, cards]); 

  // Efecto para manejar el final del juego
  useEffect(() => {
    if (pairsFound === icons.length) {
      const completionMessage = `¡Felicidades! Completaste el juego en ${attempts} intentos.`;
      
      // Mostrar mensaje de finalización
      setTimeout(() => {
        const gameMessageElement = document.getElementById('game-message');
        if (gameMessageElement) {
            gameMessageElement.textContent = completionMessage;
            gameMessageElement.classList.remove('hidden');
        }

        // Guardar el puntaje
        if (isAuthReady && bestScoreRef) {
          saveBestScore(attempts);
        }

      }, 500); 
    } else {
        // Ocultar mensaje si el juego está en curso
        const gameMessageElement = document.getElementById('game-message');
        if (gameMessageElement) {
            gameMessageElement.classList.add('hidden');
            gameMessageElement.textContent = '';
        }
    }
  }, [pairsFound, attempts, isAuthReady, bestScoreRef, saveBestScore]);

  // Función para reiniciar el juego
  const resetGame = () => {
    setAttempts(0);
    setPairsFound(0);
    setFlippedCards([]);
    setIsBusy(false);
    
    // Barajar las cartas de nuevo
    const newCards = initialCards
      .map(card => ({ ...card, isFlipped: false, isMatched: false }))
      .sort(() => Math.random() - 0.5);
    setCards(newCards);
  };

  // --- RENDERIZADO ---

  return (
    <>
        {/* Estilos CSS para la animación 3D */}
        <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');
            body { font-family: 'Inter', sans-serif; }
            .perspective {
                perspective: 1000px;
            }
            .preserve-3d {
                transform-style: preserve-3d;
            }
            .rotate-y-180 {
                transform: rotateY(180deg);
            }
            .backface-hidden {
                backface-visibility: hidden;
            }
        `}</style>
        
        <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-8 flex flex-col items-center font-sans">
            <header className="w-full max-w-lg mb-8 text-center">
                <h1 className="text-3xl font-extrabold text-indigo-400 mb-2">Juego de Memoria 🧠</h1>
                <p className="text-sm text-gray-400">Empareja {icons.length} pares. ¡Guarda tu mejor puntaje en Firestore!</p>
            </header>

            {/* Panel de Estadísticas */}
            <div className="w-full max-w-lg mb-6 grid grid-cols-3 gap-4 text-center">
                <StatCard title="INTENTOS" value={attempts} color="text-yellow-400" />
                <StatCard title="PARES" value={`${pairsFound} / ${icons.length}`} color="text-green-400" />
                <StatCard title="MEJOR" value={bestScore} color="text-pink-400" />
            </div>

            {/* Botón de Reiniciar */}
            <button
                onClick={resetGame}
                className="mb-6 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold rounded-xl shadow-lg transition duration-200 transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-indigo-500 focus:ring-opacity-50"
            >
                Reiniciar Juego 🔄
            </button>
            
            {/* Mensaje de Finalización del Juego */}
            <div id="game-message" className="hidden mb-6 p-3 w-full max-w-lg text-center bg-green-600 text-white font-semibold rounded-lg shadow-md animate-bounce">
                {/* Contenido inyectado en useEffect */}
            </div>

            {/* Tablero de Juego */}
            <div className="grid grid-cols-4 gap-3 sm:gap-4 p-4 bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full aspect-square">
                {cards.map(card => (
                    <Card
                        key={card.id}
                        card={card}
                        isFlipped={card.isFlipped || card.isMatched}
                        onClick={() => handleCardClick(card)}
                        isBusy={isBusy}
                    />
                ))}
            </div>

            {/* Estado de Firestore */}
            <div className="mt-8 text-xs text-gray-500 text-center w-full max-w-lg">
                <p className="italic">
                    <span className={firestoreStatus.includes('Error') ? 'text-red-500' : 'text-gray-400'}>
                        ⚡ {firestoreStatus}
                    </span>
                </p>
                <p className='mt-1'>
                    <span className="text-gray-500">ID de Aplicación:</span> <span className='text-gray-400 font-mono break-all'>{appId}</span>
                </p>
            </div>
        </div>
    </>
  );
};

// Componente Tarjeta
const Card = ({ card, isFlipped, onClick, isBusy }) => {
  const CardBack = () => (
    // backface-hidden y la transición de hover están en el div de atrás
    <div className="absolute inset-0 flex items-center justify-center bg-gray-700 rounded-xl backface-hidden shadow-lg border-b-4 border-gray-600 transition-all duration-300 transform hover:scale-[1.05] cursor-pointer">
      <IconSVG path="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" className="h-8 w-8 text-indigo-400 animate-pulse" />
    </div>
  );

  const CardFront = () => (
    // backface-hidden y la rotación inicial de 180 (para que esté oculta) están en el div de adelante
    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-900 rounded-xl rotate-y-180 backface-hidden shadow-xl border-b-4 border-indigo-600">
      <IconSVG path={card.icon} className={`h-10 w-10 transition-all duration-500 ${card.isMatched ? 'text-green-600' : 'text-indigo-600'}`} />
    </div>
  );

  return (
    <div 
      className={`relative w-full aspect-square perspective ${isBusy ? 'pointer-events-none' : ''}`}
      onClick={card.isMatched ? null : onClick} // No permitir clic si ya está emparejada
    >
      {/* Este div aplica la rotación total basada en isFlipped */}
      <div className={`relative w-full h-full preserve-3d transition-transform duration-500 ${isFlipped ? 'rotate-y-180' : ''}`}>
        <CardBack />
        <CardFront />
      </div>
      {/* Overlay para las cartas emparejadas */}
      {card.isMatched && (
        <div className="absolute inset-0 bg-green-500/30 rounded-xl pointer-events-none"></div>
      )}
    </div>
  );
};

// Componente de Tarjeta de Estadística
const StatCard = ({ title, value, color }) => (
  <div className="bg-gray-800 p-3 sm:p-4 rounded-xl shadow-inner border border-gray-700">
    <h3 className="text-xs sm:text-sm font-semibold text-gray-400 uppercase tracking-wider mb-1">{title}</h3>
    <p className={`text-2xl sm:text-3xl font-bold ${color}`}>
      {value}
    </p>
  </div>
);

// Exportar el componente principal
export default App;